import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { EyeOpenIcon } from '@repo/ui/icons'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getTripFolderTransfers } from '../../../api/trip-folder/get-trip-folder-transfers'
import { Paths } from '../../../lib/constants/path'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TripFolderTypeEnum } from '../../../lib/types/enums/trip-folder/trip-folder-type.enum'
import { TransferPlace } from '../../../lib/types/models/transfer/transfer-place'
import { TransferMealLight } from '../../../lib/types/models/transfer-meal/transfer-meal'
import { getStaffNameLabel } from '../../../views/staffs/utils'
import CountryFlag from '../../elements/country-flag'
import InformationToolTip from '../../elements/information-tooltip'
import TransportPicto from '../../elements/transport-picto'
import TripFolderViewTransferMeal from './trip-folder-view-transfer-meal'

interface Props {
  folderId: number
  tripFolderType?: TripFolderTypeEnum
  selectedTransfers?: TransferPlace[]
  onSelectedTransfersChange?: (transfers: TransferPlace[]) => void
}

const TripFolderTransfersSection = ({
  folderId,
  tripFolderType,
  selectedTransfers,
  onSelectedTransfersChange
}: Props) => {
  const { data: transfers, isPending } = useQuery({
    queryKey: ['trip-folder-transfers', folderId],
    queryFn: () => getTripFolderTransfers(folderId)
  })

  const { navigate } = useNavigator()
  const { modalState, toggleModal, selectedData } = useModalManager<
    'view-meal',
    TransferMealLight
  >(['view-meal'])

  const columns: Column<TransferPlace>[] = [
    {
      header: 'Moyen de transport',
      accessorKey: 'transportMeans',
      cell: ({ row }) => {
        return row.original.transfer.transportMeans?.picto ? (
          <TransportPicto picto={row.original.transfer.transportMeans?.picto} />
        ) : (
          '-'
        )
      }
    },
    {
      header: 'Prestataire voyage',
      accessorKey: 'tripProvider',
      cell: ({ row }) => valueRender(row.original.transfer.tripProvider?.companyName)
    },
    {
      header: 'Compagnie de voyage',
      accessorKey: 'travelCompany',
      cell: ({ row }) => valueRender(row.original.transfer.travelCompany?.name)
    },
    {
      header: 'N° résa',
      accessorKey: 'reservationNumber',
      cell: ({ row }) => valueRender(row.original.transfer.reservationNumber)
    },
    {
      header: '# pax réservés',
      accessorKey: 'numberOfStudents',
      cell: ({ row }) =>
        row.original.transfer.numberOfStudents + row.original.transfer.numberOfStaff
    },
    {
      header: '# pax affectés',
      accessorKey: 'numberOfStaff',
      cell: ({ row }) => {
        const numberOfStaffs =
          row.original.missionToExternalStaffs.length + row.original.missionToStaffs.length
        return row.original.numberOfPlacesDispatched + numberOfStaffs
      }
    },
    {
      header: 'AAT / Externes',
      accessorKey: 'aat',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-2 pt-2 pb-2">
            {row.original.missionToStaffs.map(({ staff }) => (
              <Badge
                key={staff.id}
                text={getStaffNameLabel(staff.firstName, staff.lastName, staff.phoneNumber)}
                className="h-fit py-1.5 w-max"
                color={tailwindColors.white.hover}
                borderColor={tailwindColors.grey.light.DEFAULT}
              />
            ))}
            {row.original.missionToExternalStaffs.map(({ firstName, lastName, phone }, index) => (
              <Badge
                key={index}
                text={getStaffNameLabel(firstName, lastName, phone)}
                className="h-fit py-1.5 w-max"
                color={tailwindColors.white.hover}
                borderColor={tailwindColors.grey.light.DEFAULT}
              />
            ))}
          </div>
        </div>
      )
    },
    {
      header: 'RDV',
      accessorKey: 'departureTimeLEC',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 py-1">
          <Badge
            text={`LEC/AAT ${valueRender(row.original.transfer.departureTimeLEC, (time) =>
              DateFormatter.getFormattedTime(time)
            )}`}
            variant="grey"
          />
          <Badge
            text={`Presta ${valueRender(row.original.transfer.departureTimeProvider, (time) =>
              DateFormatter.getFormattedTime(time)
            )}`}
            variant="grey"
          />
        </div>
      )
    },
    {
      header: 'Heure et lieu départ',
      accessorKey: 'departurePlace',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <Badge
            text={
              row.original.transfer.departureTime
                ? DateFormatter.getFormattedTime(row.original.transfer.departureTime)
                : '-'
            }
            variant="grey"
          />
          <div className="flex gap-2">
            <CountryFlag possibleCountry={row.original.transfer.departurePlace?.country} />
            <Typography.Link
              variant="secondary-body"
              onClick={() => {
                navigate(
                  Paths.TRIP_ADDRESS_UPDATE_PAGE.replace(
                    ':id',
                    row.original.transfer.departurePlace.id.toString()
                  )
                )
              }}
            >
              {row.original.transfer.departurePlace?.label}
            </Typography.Link>
          </div>
        </div>
      )
    },
    {
      header: 'Heure et lieu arrivée',
      accessorKey: 'arrivalPlace',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <Badge
            text={
              row.original.transfer.arrivalTime
                ? DateFormatter.getFormattedTime(row.original.transfer.arrivalTime)
                : '-'
            }
            variant="grey"
          />
          <div className="flex gap-2">
            <CountryFlag possibleCountry={row.original.transfer.arrivalPlace?.country} />
            <Typography.Link
              variant="secondary-body"
              onClick={() => {
                navigate(
                  Paths.TRIP_ADDRESS_UPDATE_PAGE.replace(
                    ':id',
                    row.original.transfer.arrivalPlace.id.toString()
                  )
                )
              }}
            >
              {row.original.transfer.arrivalPlace?.label}
            </Typography.Link>
          </div>
        </div>
      )
    },
    {
      header: 'Consignes',
      accessorKey: '',
      cell: ({ row }) => {
        return row.original.transfer.tripProviderGuidelineContent ? (
          <InformationToolTip
            variant="primary"
            size="small"
            triggerClassName="w-fit h-fit p-1 rounded-sm flex items-center justify-center"
          >
            <Typography
              variant="small-semibold"
              color={tailwindColors.white.DEFAULT}
              className="max-w-[200px] whitespace-normal break-words"
            >
              {row.original.transfer.tripProviderGuidelineContent}
            </Typography>
          </InformationToolTip>
        ) : (
          '-'
        )
      }
    },
    ...(tripFolderType &&
    [TripFolderTypeEnum.MAIN_RETURN, TripFolderTypeEnum.SECONDARY_RETURN].includes(tripFolderType)
      ? [
          {
            header: 'Repas',
            accessorKey: '',
            cell: ({ row }) => {
              const transferMeals = row.original.transferMeals
              return transferMeals && transferMeals.length > 0 ? (
                <div className="flex flex-col gap-1 mr-3 my-2">
                  {transferMeals.map((meal) => (
                    <div key={meal.id} className="flex gap-2">
                      <Badge
                        text={`${meal.numberOfMeals} repas - ${DateFormatter.DDMMYYYY(meal.order.sentAt)} - ${StringFormatter.capitalize(meal.order.createdBy.firstName)}`}
                        variant={meal.order.cancelledAt ? 'danger' : 'primary'}
                      />
                      <Button
                        icon={EyeOpenIcon}
                        variant="ghost"
                        className="w-fit h-fit px-1.5 py-1"
                        onClick={() => {
                          toggleModal('view-meal', meal)
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                '-'
              )
            }
          } as Column<TransferPlace>
        ]
      : [])
  ]

  if (isPending) {
    return <Skeleton className="h-[300px] w-full" />
  }

  if (!isPending && (!transfers || transfers?.length === 0)) {
    return (
      <div className="flex justify-center items-center p-8 bg-gray-50 rounded-3xl border border-primary-light">
        <Typography variant="secondary-body">Aucun transfert</Typography>
      </div>
    )
  }

  return (
    <>
      <DataTable<TransferPlace>
        selectable
        onSelectedDataChange={(selected) => {
          onSelectedTransfersChange?.(selected.isAllSelected ? (transfers ?? []) : selected.rows)
        }}
        selectedData={{
          isAllSelected: selectedTransfers?.length === transfers?.length,
          rows: selectedTransfers ?? [],
          expandedRows: []
        }}
        columns={columns}
        paginated={false}
        rowCount={transfers?.length ?? 0}
        data={transfers ?? []}
        disableHover
      />
      {selectedData && (
        <TripFolderViewTransferMeal
          isOpen={modalState['view-meal']}
          onClose={() => toggleModal('view-meal')}
          folderId={folderId}
          orderId={selectedData.order.id}
        />
      )}
    </>
  )
}

export default TripFolderTransfersSection
